import { useEffect, useState } from 'react';
import { apiClient } from '@indipia/shared/api/client';

/** 썸네일 로드 실패/URL 없음 — 대체 아이콘 */
function CameraFallbackIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
      <circle cx="12" cy="13" r="4" />
    </svg>
  );
}

/**
 * sv-photo 썸네일 로더.
 * 썸네일 URL은 인증토큰이 필요하므로 <img src>로 직접 로드하지 않고
 * apiClient(Authorization 자동 첨부)로 blob을 받아 objectURL로 표시한다.
 * 로드 실패/빈 URL이면 카메라 아이콘 플레이스홀더를 보여준다.
 */
export function ThumbImg({
  url,
  className,
  alt = '',
  imgStyle,
}: {
  url: string | null | undefined;
  className?: string;
  alt?: string;
  /**
   * 지정 시 래퍼 div 없이 `<img>` 만 이 스타일로 렌더한다(라이트박스 줌 transform 전달용).
   * 미지정 시 기존 썸네일 동작(래퍼 div + object-cover 100%).
   */
  imgStyle?: React.CSSProperties;
}) {
  const [src, setSrc] = useState<string | null>(null);
  const [failed, setFailed] = useState(!url);

  useEffect(() => {
    if (!url) {
      setFailed(true);
      return;
    }
    setFailed(false);
    // 서버 thumbUrl 은 게이트웨이 기준 전체경로(/api/worklog/...)다.
    // apiClient.baseURL 이 이미 `${API_BASE}/api` 라 그대로 넘기면 /api 가 중복된다 → 선행 /api 제거.
    const path = url.replace(/^\/api(?=\/)/, '');
    // [로컬 테스트 전용] 게스트 공유 사진(/work-order-shares/...)은 운영에선 공유쿠키로 스코프가 잡히지만
    // 로컬엔 쿠키가 없어 SHARE_NOT_FOUND 가 난다. dev 모드에 한해 주소창 shareId 를 쿼리로 실어준다(devShareParam 과 동일 규칙).
    let params: Record<string, string> | undefined;
    if (import.meta.env.DEV && path.includes('/work-order-shares/')) {
      const sid = new URLSearchParams(window.location.search).get('shareId');
      if (sid) params = { shareId: sid };
    }
    let objectUrl: string | null = null;
    const controller = new AbortController();
    apiClient
      .get<Blob>(path, { responseType: 'blob', params, signal: controller.signal })
      .then((res) => {
        objectUrl = URL.createObjectURL(res.data);
        setSrc(objectUrl);
      })
      .catch((err) => {
        if (controller.signal.aborted || err?.code === 'ERR_CANCELED') return;
        setFailed(true);
      });
    return () => {
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      setSrc(null);
    };
  }, [url]);

  // 라이트박스 등 줌 transform이 필요한 경우 — 래퍼 없이 <img>만 렌더(일반 <img> 경로와 동일 동작).
  if (imgStyle) {
    return src ? <img src={src} alt={alt} style={imgStyle} draggable={false} /> : null;
  }

  return (
    <div className={className} style={{ background: 'var(--shell-skeleton)', overflow: 'hidden' }}>
      {src && !failed && (
        <img
          src={src}
          alt={alt}
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      )}
      {failed && (
        <div className="flex h-full w-full items-center justify-center text-text-muted">
          <CameraFallbackIcon />
        </div>
      )}
    </div>
  );
}
